import React, { useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import { collection, getDocs, writeBatch, doc, serverTimestamp, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

export default function AuditTool() {
  const { currentUser } = useAuth();
  const [parsed, setParsed] = useState([]);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [auditNote, setAuditNote] = useState("");
  const [auditDate, setAuditDate] = useState(new Date().toISOString().split("T")[0]);
  const fileRef = useRef();

  useEffect(() => {
    getDocs(collection(db, "partners")).then(snap => {
      setPartners(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError(""); setParsed([]); setSuccess("");

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data;
        // Normalize headers
        const normalized = rows.map(row => {
          const name = row["name"] || row["Name"] || row["Partner"] || row["partner"] || "";
          const amount = parseFloat(
            (row["amount"] || row["Amount"] || row["Contribution"] || row["contribution"] || "0")
              .toString().replace(/[$,]/g, "")
          );
          return { name: name.trim(), amount: isNaN(amount) ? 0 : amount };
        }).filter(r => r.name);

        if (!normalized.length) {
          setError("CSV appears empty or has unrecognized column headers. Expected: name, amount");
          return;
        }

        // Match to partners
        const matched = normalized.map(row => {
          const partner = partners.find(p =>
            p.name.toLowerCase() === row.name.toLowerCase() ||
            p.email.toLowerCase() === row.name.toLowerCase()
          );
          return { ...row, matched: !!partner, partner, warning: !partner ? "No matching partner found" : null };
        });
        setParsed(matched);
      },
      error: () => setError("Failed to parse CSV. Please check the file format."),
    });
  }

  const totalContribution = parsed.reduce((s, r) => s + r.amount, 0);
  const matchedRows = parsed.filter(r => r.matched);

  async function confirmAudit() {
    setLoading(true);
    setError("");
    try {
      const batch = writeBatch(db);

      for (const row of matchedRows) {
        const partnerRef = doc(db, "partners", row.partner.id);
        const newTotal = (row.partner.totalStake || 0) + row.amount;
        batch.update(partnerRef, { totalStake: newTotal, updatedAt: serverTimestamp() });

        await addDoc(collection(db, "transactions"), {
          partnerEmail: row.partner.email,
          partnerName: row.partner.name,
          amount: row.amount,
          type: row.amount >= 0 ? "Audit Contribution" : "Audit Reduction",
          note: auditNote || "Monthly audit upload",
          date: new Date(auditDate),
          addedBy: currentUser.email,
          createdAt: serverTimestamp(),
        });
      }

      await batch.commit();

      // Log audit
      await addDoc(collection(db, "audits"), {
        date: new Date(auditDate),
        note: auditNote,
        uploadedBy: currentUser.email,
        totalContribution,
        partnerCount: matchedRows.length,
        rows: matchedRows.map(r => ({ name: r.name, amount: r.amount, email: r.partner.email })),
        createdAt: serverTimestamp(),
      });

      setSuccess(`✓ Audit confirmed! ${matchedRows.length} partner stakes updated.`);
      setParsed([]);
      setAuditNote("");
      if (fileRef.current) fileRef.current.value = "";

      // Refresh partners
      const snap = await getDocs(collection(db, "partners"));
      setPartners(snap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (err) {
      setError("Failed to save audit: " + err.message);
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  }

  return (
    <div style={styles.page} className="animate-in">
      <div style={styles.header}>
        <h1 style={styles.title}>Audit Tool</h1>
        <p style={styles.subtitle}>Upload the monthly CSV to update partner stakes</p>
      </div>

      {/* Instructions */}
      <div style={styles.infoBox}>
        <div style={styles.infoTitle}>📋 CSV Format Required</div>
        <div style={styles.infoText}>
          Your CSV must have two columns: <code style={styles.code}>name</code> and <code style={styles.code}>amount</code>.<br />
          The name must match a registered partner's name or email exactly.
        </div>
        <div style={styles.exampleBox}>
          <div style={styles.exampleRow}><span>name</span><span>amount</span></div>
          <div style={styles.exampleRow}><span>John Kim</span><span>5000</span></div>
          <div style={styles.exampleRow}><span>Justin Sam</span><span>2500.00</span></div>
        </div>
      </div>

      {/* Upload card */}
      <div className="card" style={{ marginBottom: "20px", padding: "28px" }}>
        <h2 style={styles.cardTitle}>Upload Audit CSV</h2>

        <div style={styles.formRow}>
          <div style={{ flex: 1 }}>
            <label style={styles.label}>Audit Date</label>
            <input
              type="date"
              value={auditDate}
              onChange={e => setAuditDate(e.target.value)}
              className="input-field"
            />
          </div>
          <div style={{ flex: 2 }}>
            <label style={styles.label}>Note (optional)</label>
            <input
              type="text"
              value={auditNote}
              onChange={e => setAuditNote(e.target.value)}
              placeholder="e.g. April 2026 monthly audit"
              className="input-field"
            />
          </div>
        </div>

        <div style={{ marginTop: "16px" }}>
          <label style={styles.label}>CSV File</label>
          <div
            style={styles.dropZone}
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: "none" }} />
            <div style={styles.dropIcon}>⊞</div>
            <div style={styles.dropText}>Click to upload CSV file</div>
            <div style={styles.dropSub}>Supported format: .csv</div>
          </div>
        </div>

        {error && (
          <div style={styles.errorBox}><span style={{ color: "var(--red)" }}>⚠</span> {error}</div>
        )}
        {success && (
          <div style={styles.successBox}>{success}</div>
        )}
      </div>

      {/* Preview */}
      {parsed.length > 0 && (
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h2 style={styles.cardTitle}>Preview & Confirm</h2>
              <p style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "4px" }}>
                Review the changes before confirming. Only matched partners will be updated.
              </p>
            </div>
            <div style={styles.summaryBadge}>
              Total: <strong>${totalContribution.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>
            </div>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                {["Partner Name", "Amount", "Status", "Current Stake", "New Stake"].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsed.map((row, i) => (
                <tr key={i}>
                  <td style={styles.td}><strong>{row.name}</strong></td>
                  <td style={{ ...styles.td, color: row.amount >= 0 ? "var(--green)" : "var(--red)", fontWeight: 700 }}>
                    {row.amount >= 0 ? "+" : ""}${row.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </td>
                  <td style={styles.td}>
                    {row.matched ? (
                      <span style={styles.matchedBadge}>✓ Matched</span>
                    ) : (
                      <span style={styles.unmatchedBadge}>✗ No match</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {row.matched ? `$${(row.partner.totalStake || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 700, color: "var(--accent-bright)" }}>
                    {row.matched ? `$${((row.partner.totalStake || 0) + row.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: "flex", gap: "12px", marginTop: "24px", justifyContent: "flex-end" }}>
            <button className="btn-secondary" onClick={() => { setParsed([]); if (fileRef.current) fileRef.current.value = ""; }}>
              Cancel
            </button>
            {!confirming ? (
              <button className="btn-primary" onClick={() => setConfirming(true)} disabled={matchedRows.length === 0}>
                Confirm Audit ({matchedRows.length} partners)
              </button>
            ) : (
              <div style={styles.confirmBox}>
                <span style={{ color: "var(--gold)", fontSize: "13px" }}>⚠ This will permanently update partner stakes. Are you sure?</span>
                <button className="btn-danger" onClick={confirmAudit} disabled={loading}>
                  {loading ? "Saving..." : "Yes, Confirm"}
                </button>
                <button className="btn-secondary" onClick={() => setConfirming(false)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: "900px", margin: "0 auto" },
  header: { marginBottom: "24px" },
  title: { fontSize: "28px", fontFamily: "var(--font-display)", fontWeight: 800 },
  subtitle: { color: "var(--text-muted)", fontSize: "14px", marginTop: "4px" },
  infoBox: {
    background: "var(--accent-dim)", border: "1px solid var(--border-accent)",
    borderRadius: "var(--radius-md)", padding: "16px 20px", marginBottom: "20px",
  },
  infoTitle: { fontWeight: 700, color: "var(--accent-bright)", marginBottom: "6px", fontSize: "14px" },
  infoText: { color: "var(--text-secondary)", fontSize: "13px", lineHeight: 1.7 },
  code: { background: "rgba(108,99,255,0.2)", padding: "1px 6px", borderRadius: "4px", fontFamily: "monospace", fontSize: "12px" },
  exampleBox: {
    marginTop: "12px", background: "var(--bg-secondary)", borderRadius: "var(--radius-sm)",
    padding: "10px 14px", fontFamily: "monospace", fontSize: "12px", color: "var(--text-secondary)",
  },
  exampleRow: { display: "flex", gap: "40px", padding: "2px 0" },
  cardTitle: { fontSize: "16px", fontFamily: "var(--font-display)", fontWeight: 700 },
  formRow: { display: "flex", gap: "16px", marginTop: "20px", flexWrap: "wrap" },
  label: { display: "block", fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" },
  dropZone: {
    border: "2px dashed var(--border-accent)", borderRadius: "var(--radius-md)",
    padding: "32px", textAlign: "center", cursor: "pointer",
    transition: "var(--transition)", background: "var(--bg-secondary)",
    marginTop: "8px",
  },
  dropIcon: { fontSize: "28px", color: "var(--accent)", marginBottom: "8px" },
  dropText: { color: "var(--text-primary)", fontWeight: 600, marginBottom: "4px" },
  dropSub: { color: "var(--text-muted)", fontSize: "12px" },
  errorBox: {
    background: "var(--red-dim)", border: "1px solid rgba(255,92,108,0.2)",
    borderRadius: "var(--radius-sm)", padding: "12px 16px", fontSize: "13px",
    color: "var(--text-secondary)", display: "flex", gap: "8px", alignItems: "center",
    marginTop: "16px",
  },
  successBox: {
    background: "var(--green-dim)", border: "1px solid rgba(0,214,143,0.2)",
    borderRadius: "var(--radius-sm)", padding: "12px 16px", fontSize: "13px",
    color: "var(--green)", marginTop: "16px", fontWeight: 600,
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", padding: "10px 12px", fontSize: "11px",
    color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em",
    borderBottom: "1px solid var(--border)",
  },
  td: { padding: "12px 12px", fontSize: "14px", color: "var(--text-primary)", borderBottom: "1px solid rgba(255,255,255,0.03)" },
  matchedBadge: { background: "var(--green-dim)", color: "var(--green)", padding: "3px 10px", borderRadius: "100px", fontSize: "11px", fontWeight: 700 },
  unmatchedBadge: { background: "var(--red-dim)", color: "var(--red)", padding: "3px 10px", borderRadius: "100px", fontSize: "11px", fontWeight: 700 },
  summaryBadge: {
    background: "var(--accent-dim)", border: "1px solid var(--border-accent)",
    borderRadius: "var(--radius-sm)", padding: "8px 16px", fontSize: "14px", color: "var(--accent-bright)",
  },
  confirmBox: {
    display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap",
    background: "var(--gold-dim)", border: "1px solid rgba(255,209,102,0.2)",
    borderRadius: "var(--radius-sm)", padding: "12px 16px",
  },
};
